import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout as AntLayout, Menu, Avatar, Dropdown, Badge, Typography, Space, Button } from 'antd'
import {
  MenuOutlined,
  DashboardOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  RocketOutlined,
  LineChartOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useAppSelector, useAppDispatch } from '../hooks/redux'
import { logout } from '../store/slices/authSlice'
import { isAdminUser } from '../utils/adminUtils'

const { Header, Sider, Content } = AntLayout
const { Text } = Typography

function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const [collapsed, setCollapsed] = useState(false)

  const handleLogout = async () => {
    await dispatch(logout())
    navigate('/login')
  }

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/lead',
      icon: <FileTextOutlined />,
      label: 'Process Lead',
    },
    {
      key: '/stats',
      icon: <BarChartOutlined />,
      label: 'Statistics',
    },
    {
      key: '/timeline',
      icon: <ClockCircleOutlined />,
      label: 'Timeline',
    },
    {
      key: '/marketing',
      icon: <RocketOutlined />,
      label: 'Marketing',
    },
    {
      key: '/analytics',
      icon: <LineChartOutlined />,
      label: 'Analytics',
    },
  ]

  // Check if user is admin (either through ADMIN_EMAILS or Django admin privileges)
  if (user?.is_admin || user?.is_staff || user?.is_superuser) {
    menuItems.push({
      key: '/admin',
      icon: <SettingOutlined />,
      label: 'Admin Panel',
    })
  }

  if (isAdminUser(user)) {
    menuItems.push({
      key: '/admin/import-leads',
      icon: <UploadOutlined />,
      label: 'Import Leads',
    })
  }

  const userMenuItems: any[] = [
    {
      key: 'user-info',
      label: (
        <div>
          <div>{user?.name}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{user?.email}</Text>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ]

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div style={{ 
          height: '64px', 
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Typography.Title level={5} style={{ margin: 0, color: 'white' }}>
            {collapsed ? 'LG' : 'Lead Gen Console'}
          </Typography.Title>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      
      <AntLayout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin 0.2s' }}>
        <Header style={{ 
          padding: '0 24px', 
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <Space size="large">
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
            <Typography.Title level={4} style={{ margin: 0 }}>
              {menuItems.find((item) => item.key === location.pathname)?.label || 'Lead Generation'}
            </Typography.Title>
          </Space>
          
          <Space size="middle">
            <Badge count={user?.daily_leads_completed || 0} showZero>
              <Space>
                <Text>Today's Leads</Text>
              </Space>
            </Badge>
            
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar 
                style={{ cursor: 'pointer', backgroundColor: '#1976d2' }}
                icon={<UserOutlined />}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </Dropdown>
          </Space>
        </Header>
        
        <Content style={{
          margin: '24px',
          padding: 24,
          minHeight: 280,
          background: '#fff',
          borderRadius: '8px',
        }}>
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  )
}

export default Layout
